import { LatestImageInfo } from './latest_image_info'
import { MediaImageFallbackHandler } from './media_image_fallback_handler'
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { MediaImageStore } from '@/shared/sync/mediaRepository/MediaImageStore'
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle"
import { MediaImageRepositoryPathAdapter } from '@/shared/sync/mediaRepository/MediaImageRepositoryPathAdapter'
import { MediaImageRepositoryProcessingDOM } from './MediaImageRepositoryProcessingDOM'
import { FileSystemRecord } from '@/shared/sync/mediaRepository/FileSystemRecord'
import ApiService from '@services/api'

/**
 * Base: storage-only responsibilities (no image processing).
 */

/**
 * Subclass: adds processing (base64<->blob URL, resizing, mask extraction, etc.).
 * Keeps the original public API/singleton name to minimize call-site changes.
 */

export class MediaImageRepository extends MediaImageStore {
  _mediaImageRepositoryProcessing: MediaImageRepositoryProcessingDOM = new MediaImageRepositoryProcessingDOM()

  async clone(newProjectId: string, pathAdapater: MediaImageRepositoryPathAdapter): Promise<MediaImageRepository> {
    const clonedRepository = new MediaImageRepository(newProjectId, pathAdapater)
    clonedRepository._mediaImageRepositoryProcessing = this._mediaImageRepositoryProcessing

    await this.filesystem.clone(clonedRepository.getProjectId())
    
    return clonedRepository
  }

  async getRecord(
      layerId: string,
      layerStateId: string,
      imgType: MediaImageType
    ): Promise<FileSystemRecord | null> {
      const record = await super.getRecord(layerId, layerStateId, imgType)
      if (record && await record.getBlobUrl()) {
        return record;
      }
      const fallbackRecord = await MediaImageFallbackHandler.getInstance().fallback(this, layerId, layerStateId, imgType)
      return fallbackRecord
    }

  async storeLatestImageInfo(
    layer: string,
    layerStateId: string,
    latestImageInfo: LatestImageInfo
  ): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(async () => {
        let success = true
        success =
          success && (await this.storeImageBase64(layer, layerStateId, MediaImageType.fitted, latestImageInfo.fittedImage.toDataURL()))
        success =
          success && (await this.storeImageBase64(layer, layerStateId, MediaImageType.fittedMask, latestImageInfo.fittedMask.toDataURL()))
        success =
          success && (await this.storeImageBase64(layer, layerStateId, MediaImageType.latest, latestImageInfo.latestImage.toDataURL()))
        success =
          success && (await this.storeImageBase64(layer, layerStateId, MediaImageType.mask, latestImageInfo.mask.toDataURL()))
        resolve(success)
      })
    })
  }

  async storeImageUrl(
    layer: string,
    layerStateId: string,
    imageType: MediaImageType,
    imageUrl: URL | null,
    abortSignal?: AbortSignal
  ): Promise<boolean> {
    if (!imageUrl) return Promise.resolve(false)
    const blobUrl = await this.getDataBlob(imageUrl, abortSignal)
    if (!blobUrl) return false
    return this.storeImageBlobString(layer, layerStateId, imageType, blobUrl)
  }

  async storeImageBase64(
    layer: string,
    layerStateId: string,
    imageType: MediaImageType,
    imageBase64: string,
    abortSignal?: AbortSignal
  ): Promise<boolean> {
    const blobUrl = await this._mediaImageRepositoryProcessing.base64ToBlobUrl(imageBase64)
    return this.storeImageBlobString(layer, layerStateId, imageType, blobUrl)
  }

  async getImageBase64(layer: string, layerStateId: string, imageType: MediaImageType): Promise<string> {
    const blobUrl = await this.getImage(layer, layerStateId, imageType)
    const base64String = await this._mediaImageRepositoryProcessing.blobUrlToBase64(blobUrl)
    return base64String
  }

  async generateLatestImageInfo(
    layer: string,
    layerStateId: string,
    threshold: number | null = null,
    absolueBoundingBox?: Rectangle
  ): Promise<LatestImageInfo> {
    const original = await this.getImage(layer, layerStateId, MediaImageType.original)
    const mask = await this.getImage(layer, layerStateId, MediaImageType.mask)
    const result = await this._mediaImageRepositoryProcessing.generateLatestImageInfo(original, mask, threshold, absolueBoundingBox)
    return result
  }

  async getDataBlob(url: URL, signal?: AbortSignal): Promise<string | null> {
    try {
      const blob = await ApiService.downloadAsset(url, { responseType: 'blob', signal })
      if (signal?.aborted) return null

      const imageFormats = ['image/png', 'image/jpeg', 'image/jpg']
      const objectUrl = URL.createObjectURL(blob.data)

      if (blob.data.size > 2097152 && imageFormats.indexOf(blob.data.type) !== -1) {
        // > 2MB: downscale before storing
        return this._mediaImageRepositoryProcessing.resizeBlobToMaxEdgeSize(objectUrl, 1280, signal)
      } else {
        return objectUrl
      }
    } catch {
      return null
    }
  }

  async clear(idsToPreserve: string[] = []): Promise<void> {
    const prefix = this.getPathForClearing();
    await this.filesystem.clearByPrefix(prefix, idsToPreserve);
  }
}